import { motion } from 'framer-motion'
import { AlertCircle, Globe } from 'lucide-react'
import { Button } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { formStyles } from '@/constants/styles'
import type { AttestationScreenBaseProps } from './AttestationScreenBase'

// Background images from Figma
// TODO: Download and replace with local assets if needed
const imgEllipse947 = "https://www.figma.com/api/mcp/asset/467ea35d-7806-42c8-a033-946204814e2f"
const imgGroup691314559 = "https://www.figma.com/api/mcp/asset/610b71be-2783-48e9-ba2c-d990015b9aaf"

export const AttestationScreenDesktop = ({
  formData,
  loading,
  error,
  onFormDataChange,
  onSubmit,
}: AttestationScreenBaseProps) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-[#f2f1f6] to-[#eae9ec] relative flex items-center justify-center"
    >
      {/* Background ellipse */}
      <div className="absolute bottom-[-259px] left-0 w-[985px] h-[985px]">
        <div className="absolute inset-[-20.3%]">
          <img 
            alt="" 
            className="block max-w-none w-full h-full object-cover" 
            src={imgEllipse947} 
          />
        </div>
      </div>

      {/* Title */}
      <p className="absolute font-['Plus_Jakarta_Sans',sans-serif] font-medium leading-[48px] left-1/2 text-[#2d2923] text-[36px] text-center top-[112px] -translate-x-1/2 w-[520px] tracking-[-0.52px] whitespace-pre-wrap">
        Account Verification Required
      </p>

      {/* Subtitle */}
      <p className="absolute font-['Plus_Jakarta_Sans',sans-serif] font-normal leading-[24px] left-1/2 text-[#2d2923] text-[16px] text-center top-[168px] -translate-x-1/2 w-[520px] whitespace-pre-wrap">
        Please complete the following attestation to access your account
      </p>

      {/* Main card */}
      <div className="absolute bg-white border-2 border-solid border-white h-[570px] left-1/2 overflow-hidden top-[252px] -translate-x-1/2 w-[654px] rounded-xl">
        {/* Background image in card */}
        <div className="absolute h-[376px] left-[-207px] top-[138px] w-[1102px]">
          <div className="absolute inset-[-27.66%_-10.53%_-30.32%_-10.53%]">
            <img 
              alt="" 
              className="block max-w-none w-full h-full object-cover" 
              src={imgGroup691314559} 
            />
          </div>
        </div>

        {/* Form content */}
        <div className="relative z-10 h-full flex flex-col">
          <form onSubmit={onSubmit} className="flex-1 flex flex-col px-12 py-10">
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded mb-4">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Income Verification Section */}
            <div className="mb-6">
              <h3 className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[16px] text-[#2d2923] mb-4 leading-[24px]">
                Income Verification
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="individual_income_test"
                    checked={formData.individual_income_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ individual_income_test: checked as boolean })
                    }}
                    className={formStyles.checkbox}
                  />
                  <label htmlFor="individual_income_test" className="cursor-pointer">
                    <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
                      My annual individual income exceeds $200,000
                    </span>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="joint_income_test"
                    checked={formData.joint_income_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ joint_income_test: checked as boolean })
                    }}
                    className={formStyles.checkbox}
                  />
                  <label htmlFor="joint_income_test" className="cursor-pointer">
                    <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
                      My annual joint household income exceed $300,000
                    </span>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="net_worth_test"
                    checked={formData.net_worth_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ net_worth_test: checked as boolean })
                    }}
                    className={formStyles.checkbox}
                  />
                  <label htmlFor="net_worth_test" className="cursor-pointer">
                    <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
                      My net worth exceeds $1 million (excluding primary residence)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Country of Origin */}
            <div className="mb-6">
              <label className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] mb-2 block leading-[20px]">
                Country of Origin
              </label>
              <div className="relative">
                <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgba(45,41,35,0.72)] z-10" />
                <Select
                  value={formData.country}
                  onValueChange={(value) => {
                    onFormDataChange({ country: value })
                  }}
                >
                  <SelectTrigger className="w-full bg-white border border-[rgba(45,41,35,0.12)] pl-10 h-[48px] text-[14px] text-[rgba(45,41,35,0.72)]">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)] shadow-lg">
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="United Arab Emirates">UAE</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="Brazil">Brazil</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="Hong Kong">Hong Kong</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="Japan">Japan</SelectItem>
                    <SelectItem value="Singapore">Singapore</SelectItem>
                    <SelectItem value="South Korea">South Korea</SelectItem>
                    <SelectItem value="Switzerland">Switzerland</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Market Insights */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="market_insights_opt_in"
                  checked={formData.market_insights_opt_in}
                  onCheckedChange={(checked) => {
                    onFormDataChange({ market_insights_opt_in: checked as boolean })
                  }}
                  className={formStyles.checkbox}
                />
                <label htmlFor="market_insights_opt_in" className="cursor-pointer">
                  <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
                    Receive monthly market insights
                  </span>
                </label>
              </div>
            </div>

            {/* Affirmation */}
            <div className="mb-6">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="affirmation"
                  checked={formData.affirmation}
                  onCheckedChange={(checked) => {
                    onFormDataChange({ affirmation: checked as boolean })
                  }}
                  className={formStyles.checkbox}
                />
                <label htmlFor="affirmation" className="cursor-pointer flex-1">
                  <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px] block">
                    I affirm that the information provided is true and accurate *
                  </span>
                  <p className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[12px] text-[rgba(45,41,35,0.72)] mt-1 leading-[16px]">
                    You must check this box to proceed
                  </p>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-auto">
              <Button
                type="submit"
                variant="primary"
                className="w-full h-12 bg-[rgba(45,41,35,0.72)] hover:bg-[rgba(45,41,35,0.85)] text-white hover:text-white font-plus-jakarta font-medium text-[14px] leading-[20px] rounded-lg transition-all duration-200 disabled:opacity-50"
                isLoading={loading}
                disabled={!formData.affirmation || !formData.country}
              >
                SUBMIT ATTESTATION
              </Button>

              {/* Disclaimer */}
              <p className="font-['Plus_Jakarta_Sans',sans-serif] font-medium leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] text-center mt-6 w-full whitespace-pre-wrap">
                This information is required for compliance purposes and to ensure proper access to our platform
              </p>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  )
}

